import { createElement, useCallback, useContext, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useActorNames, type Actor } from '@/shared/details'
import { fieldErrorText, FormRow } from '@/shared/forms'
import { Checkbox, cn, FieldLabelContext, Select, TextField } from '@/shared/ui'
import { focusRing, targetSize } from '@/shared/ui/cn'
import { FieldError } from '@/shared/ui/FieldError'
import { X } from '@/shared/ui/icons'

import { usePersonChooser } from './person-chooser'
import { textLimit, type CustomFieldDefinition, type CustomFieldFormValues } from './types'
import { groupCustomFields, offeredValues } from './values'

const noValues: CustomFieldFormValues = {}
const noChoices: readonly string[] = []

type NameOf = ReturnType<typeof useActorNames>

/**
 * A record's custom fields in its create or edit form (`RecordForm`), by display group: the
 * record's own fields first, then each group under its name, each field a `FormRow` from the
 * mockups' `edit-create/UserForm.jsx`. The values sit in the form under `name`, by field id
 * (`toFormValues`), checked by `customFieldsSchema`. `group` draws one group only, so an owner
 * can put a group inside one of its own sections.
 */
export function CustomFieldInputs(props: {
  readonly definitions: readonly CustomFieldDefinition[]
  /** The form field holding the values; `customFields` by default. */
  readonly name?: string
  readonly group?: string | null
}) {
  const name = props.name ?? 'customFields'
  const groups = groupCustomFields(props.definitions, props.group)
  // Every value, so a dropdown offers what its dependencies allow.
  const form = (useWatch({ name }) ?? noValues) as CustomFieldFormValues
  // Every shown person field's name in one lookup.
  const people: Actor[] = groups.flatMap((group) => group.fields).flatMap((definition) => {
    const value = form[definition.id]
    return definition.type === 'person' && typeof value === 'string' && value !== '' ? [{ type: 'user' as const, id: value }] : []
  })
  const nameOf = useActorNames(people)

  const rows = (fields: readonly CustomFieldDefinition[]) =>
    fields.map((field) => (
      <CustomFieldRow
        key={field.id}
        field={field}
        path={`${name}.${field.id}`}
        definitions={props.definitions}
        form={form}
        nameOf={nameOf}
      />
    ))

  return (
    <>
      {groups.map((group) =>
        group.name === null ? (
          <div key="" className="flex flex-col gap-1">
            {rows(group.fields)}
          </div>
        ) : (
          <fieldset key={group.name} className="mt-6 flex flex-col gap-1">
            <legend className="mb-3 font-heading text-lg font-medium text-text-primary">{group.name}</legend>
            {rows(group.fields)}
          </fieldset>
        ),
      )}
    </>
  )
}

function CustomFieldRow(props: {
  readonly field: CustomFieldDefinition
  readonly path: string
  readonly definitions: readonly CustomFieldDefinition[]
  readonly form: CustomFieldFormValues
  readonly nameOf: NameOf
}) {
  const intl = useIntl()
  const hintId = useId()
  const { field, path } = props
  const hints = [
    field.tooltip?.trim() ?? '',
    field.type === 'text' ? intl.formatMessage({ id: 'customFields.hint.maxLength' }, { max: field.maxLength ?? textLimit }) : '',
  ].filter((hint) => hint !== '')
  const describedBy = hints.length > 0 ? hintId : undefined

  let control: ReactNode
  switch (field.type) {
    case 'text':
    case 'number':
      control = <TextControl path={path} numeric={field.type === 'number'} describedBy={describedBy} />
      break
    case 'checkbox':
      control = <CheckboxControl path={path} describedBy={describedBy} />
      break
    case 'dropdown':
      control = <DropdownControl path={path} offered={offeredValues(field, props.definitions, props.form)} describedBy={describedBy} />
      break
    case 'checkboxList':
      control = <CheckboxListControl path={path} field={field} describedBy={describedBy} />
      break
    case 'person':
      control = <PersonControl path={path} field={field} describedBy={describedBy} nameOf={props.nameOf} />
      break
  }

  return (
    <FormRow label={field.name} required={field.required}>
      <div className="flex w-full max-w-md flex-col gap-1">
        {control}
        {hints.length > 0 && (
          <p id={hintId} className="text-xs text-text-secondary">
            {hints.join(' ')}
          </p>
        )}
      </div>
    </FormRow>
  )
}

/** A bound field's error, ready to show. */
function useError(path: string): string | undefined {
  const intl = useIntl()
  const { fieldState } = useController({ name: path })
  return fieldErrorText(intl, fieldState.error)
}

function TextControl(props: { readonly path: string; readonly numeric: boolean; readonly describedBy: string | undefined }) {
  const { register } = useFormContext()
  const error = useError(props.path)
  return (
    <TextField
      {...register(props.path)}
      {...(props.numeric ? { inputMode: 'numeric' as const } : {})}
      aria-describedby={props.describedBy}
      {...(error === undefined ? {} : { error })}
    />
  )
}

function CheckboxControl(props: { readonly path: string; readonly describedBy: string | undefined }) {
  const intl = useIntl()
  const { field, fieldState } = useController({ name: props.path })
  const error = fieldErrorText(intl, fieldState.error)
  const { onChange } = field
  const change = useCallback((checked: boolean) => onChange(checked), [onChange])
  return (
    <Checkbox
      checked={field.value === true}
      onChange={change}
      name={field.name}
      onBlur={field.onBlur}
      inputRef={field.ref}
      {...(props.describedBy === undefined ? {} : { describedBy: props.describedBy })}
      {...(error === undefined ? {} : { error })}
    />
  )
}

function DropdownControl(props: {
  readonly path: string
  readonly offered: readonly string[]
  readonly describedBy: string | undefined
}) {
  const intl = useIntl()
  const { field, fieldState } = useController({ name: props.path })
  const error = fieldErrorText(intl, fieldState.error)
  const value = typeof field.value === 'string' ? field.value : ''
  const options = useMemo(
    () => [
      { value: '', label: intl.formatMessage({ id: 'customFields.notSet' }) },
      // A value no longer offered stays shown until changed; saving it is refused.
      ...(value === '' || props.offered.includes(value) ? props.offered : [...props.offered, value]).map((item) => ({
        value: item,
        label: item,
      })),
    ],
    [intl, props.offered, value],
  )
  return (
    <Select<string>
      value={value}
      onChange={field.onChange}
      name={field.name}
      onBlur={field.onBlur}
      inputRef={field.ref}
      options={options}
      {...(props.describedBy === undefined ? {} : { describedBy: props.describedBy })}
      {...(error === undefined ? {} : { error })}
    />
  )
}

function CheckboxListControl(props: {
  readonly path: string
  readonly field: CustomFieldDefinition
  readonly describedBy: string | undefined
}) {
  const intl = useIntl()
  const errorId = useId()
  const rowLabelId = useContext(FieldLabelContext)
  const { field, fieldState } = useController({ name: props.path })
  const error = fieldErrorText(intl, fieldState.error)
  const chosen = Array.isArray(field.value) ? (field.value as readonly string[]) : noChoices
  const { onChange } = field
  const describedBy = [props.describedBy, error === undefined ? undefined : errorId].filter(Boolean).join(' ')
  const { listValues } = props.field
  // A value chosen before the list dropped it shows, so it can be cleared (saving it is refused),
  // and stays once cleared, so focus is not lost with its box.
  const [opened] = useState(() => chosen)
  const shown = useMemo(() => {
    const listed = listValues.map((item) => item.value)
    return [...new Set([...listed, ...opened, ...chosen])]
  }, [listValues, opened, chosen])

  return (
    <fieldset
      {...(rowLabelId === null ? {} : { 'aria-labelledby': rowLabelId })}
      {...(describedBy === '' ? {} : { 'aria-describedby': describedBy })}
      className="flex flex-col gap-2"
    >
      {shown.map((value, index) => (
        <ListCheckbox
          key={value}
          value={value}
          chosen={chosen}
          onChange={onChange}
          invalid={error !== undefined}
          {...(index === 0 ? { inputRef: field.ref, onBlur: field.onBlur } : {})}
        />
      ))}
      {error !== undefined && <FieldError id={errorId} message={error} />}
    </fieldset>
  )
}

function ListCheckbox(props: {
  readonly value: string
  readonly chosen: readonly string[]
  readonly onChange: (value: readonly string[]) => void
  readonly invalid: boolean
  readonly inputRef?: (element: HTMLInputElement | null) => void
  readonly onBlur?: () => void
}) {
  const { value, chosen, onChange } = props
  const toggle = useCallback(
    (checked: boolean) => onChange(checked ? [...chosen, value] : chosen.filter((item) => item !== value)),
    [value, chosen, onChange],
  )
  return (
    <Checkbox
      checked={chosen.includes(value)}
      onChange={toggle}
      label={value}
      invalid={props.invalid}
      {...(props.inputRef === undefined ? {} : { inputRef: props.inputRef })}
      {...(props.onBlur === undefined ? {} : { onBlur: props.onBlur })}
    />
  )
}

/**
 * A person, as the mockups' chosen chip: the person's name in a read-only field, a clear button,
 * and Choose, which opens the person chooser slot's dialog.
 */
function PersonControl(props: {
  readonly path: string
  readonly field: CustomFieldDefinition
  readonly describedBy: string | undefined
  readonly nameOf: NameOf
}) {
  const intl = useIntl()
  const Chooser = usePersonChooser()
  const { field, fieldState } = useController({ name: props.path })
  const error = fieldErrorText(intl, fieldState.error)
  const value = typeof field.value === 'string' && field.value !== '' ? field.value : null
  const [open, setOpen] = useState(false)
  // The names of people chosen here, before any lookup knows them.
  const [chosenNames, setChosenNames] = useState<ReadonlyMap<string, string>>(() => new Map())
  const looked = value === null ? null : props.nameOf({ type: 'user', id: value })
  // A name still loading says so, and only a person with no name to show is unknown.
  const shown =
    value === null
      ? intl.formatMessage({ id: 'customFields.notSet' })
      : (chosenNames.get(value) ??
        (looked === undefined
          ? intl.formatMessage({ id: 'customFields.person.loading' })
          : (looked?.name ?? intl.formatMessage({ id: 'customFields.person.unknown' }))))

  const { onChange, onBlur, ref: fieldRef } = field
  const choose = useCallback(
    (person: { readonly id: string; readonly name: string }) => {
      setChosenNames((names) => new Map(names).set(person.id, person.name))
      onChange(person.id)
      setOpen(false)
    },
    [onChange],
  )
  // Clearing removes the clear button, so focus moves to the field first.
  const input = useRef<HTMLInputElement | null>(null)
  const setInput = useCallback(
    (element: HTMLInputElement | null) => {
      input.current = element
      fieldRef(element)
    },
    [fieldRef],
  )
  const clear = useCallback(() => {
    input.current?.focus()
    onChange(null)
  }, [onChange])
  const close = useCallback(() => setOpen(false), [])
  const openChooser = useCallback(() => setOpen(true), [])
  const buttonClass = cn('cursor-pointer rounded-xs text-sm text-primary-main hover:underline', targetSize, focusRing)

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <TextField
          readOnly
          value={shown}
          ref={setInput}
          onBlur={onBlur}
          aria-describedby={props.describedBy}
          className="min-w-[12rem] flex-1"
          {...(error === undefined ? {} : { error })}
        />
        {value !== null && (
          <button
            type="button"
            onClick={clear}
            aria-label={intl.formatMessage({ id: 'customFields.person.clear' }, { field: props.field.name })}
            className={cn('cursor-pointer rounded-xs text-text-secondary hover:text-text-primary', targetSize, focusRing)}
          >
            <X size={14} />
          </button>
        )}
        {Chooser !== null && (
          <button
            type="button"
            onClick={openChooser}
            aria-label={intl.formatMessage({ id: 'customFields.person.choose' }, { field: props.field.name })}
            className={buttonClass}
          >
            {intl.formatMessage({ id: 'customFields.person.chooseShort' })}
          </button>
        )}
      </div>
      {Chooser === null && (
        <p className="text-xs text-text-secondary">{intl.formatMessage({ id: 'customFields.person.unavailable' })}</p>
      )}
      {Chooser !== null &&
        createElement(Chooser, {
          open,
          title: intl.formatMessage({ id: 'customFields.person.choose' }, { field: props.field.name }),
          selected: value,
          onClose: close,
          onChoose: choose,
        })}
    </div>
  )
}

